import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EDITOR_ROLES } from "@shared/core/types";
import type { ExtractionLot } from "@shared/modules/x-extraction";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Badge } from "../../../core/components/ui/badge";
import { Modal } from "../../../core/components/ui/modal";
import { Field, Input, Textarea } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { allowedFor } from "../../../core/modules";
import { createLot, listLots } from "../lib/api";

function progress(l: ExtractionLot): { done: number; pending: number; failed: number } {
  const c = l.counts;
  return {
    done: (c.a_valider ?? 0) + (c.applique ?? 0) + (c.rejete ?? 0),
    pending: (c.en_attente ?? 0) + (c.en_cours ?? 0),
    failed: c.echec ?? 0,
  };
}

export function LotsPage() {
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const navigate = useNavigate();
  const canEdit = allowedFor({ roles: EDITOR_ROLES }, user?.role);
  const [lots, setLots] = useState<ExtractionLot[] | null>(null);
  const [archives, setArchives] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLots(await listLots(archives));
    } catch {
      push("danger", "Impossible de charger les lots");
    }
  }, [archives, push]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const lot = await createLot({ name: name.trim(), description: description.trim() || null });
      navigate(`/analyse/${lot.id}`);
    } catch {
      push("danger", "Création du lot impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Analyse de CV"
        subtitle="Déposez un lot de CV : chacun est lu sur le serveur, structuré (expériences, formations, compétences) et comparé dans un rapport."
        actions={
          canEdit ? (
            <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => setCreating(true)}>
              Nouveau lot
            </Button>
          ) : undefined
        }
      />

      <table className="table">
        <thead>
          <tr>
            <th>Lot</th>
            <th>CV</th>
            <th>Avancement</th>
            <th className="table__col--wide">Créé</th>
          </tr>
        </thead>
        <tbody>
          {(lots ?? []).map((l) => {
            const p = progress(l);
            return (
              <tr key={l.id} className="table__row--link" onClick={() => navigate(`/analyse/${l.id}`)}>
                <td>
                  <button type="button" className="analyses__name" onClick={() => navigate(`/analyse/${l.id}`)}>
                    {l.name}
                  </button>
                  {l.description ? <span className="analyses__sub">{l.description}</span> : null}
                </td>
                <td className="analyses__num">{l.total}</td>
                <td>
                  {l.total === 0 ? (
                    <span className="muted">aucun CV</span>
                  ) : p.pending ? (
                    <Badge variant="primary">
                      {p.done} / {l.total} lus
                    </Badge>
                  ) : (
                    <Badge variant="success">terminé</Badge>
                  )}
                  {p.failed ? (
                    <>
                      {" "}
                      <Badge variant="danger">
                        {p.failed} échec{p.failed > 1 ? "s" : ""}
                      </Badge>
                    </>
                  ) : null}
                  {l.archivedAt ? (
                    <>
                      {" "}
                      <Badge>archivé</Badge>
                    </>
                  ) : null}
                </td>
                <td className="table__col--wide">{formatDateTime(l.createdAt, { dateStyle: "medium", timeStyle: "short" })}</td>
              </tr>
            );
          })}
          {lots && lots.length === 0 ? (
            <tr>
              <td colSpan={4} className="table__empty">
                Aucun lot{archives ? "" : " en cours"}. {canEdit ? "Créez-en un pour déposer des CV." : null}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      <div className="lots__footer">
        <Button variant="ghost" size="sm" onClick={() => setArchives(!archives)}>
          {archives ? "Masquer les lots archivés" : "Afficher les lots archivés"}
        </Button>
      </div>

      <Modal
        open={creating}
        title="Nouveau lot d'analyse"
        onClose={() => setCreating(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Annuler
            </Button>
            <Button variant="primary" disabled={busy || !name.trim()} onClick={create}>
              Créer et déposer des CV
            </Button>
          </>
        }
      >
        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void create();
          }}
        >
          <Field label="Nom du lot" hint="Le poste ou la campagne, jamais le nom d'un candidat (ex. « Soudeurs — novembre »).">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus />
          </Field>
          <Field label="Description (facultatif)">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
          </Field>
        </form>
      </Modal>
    </div>
  );
}
